import { supabase } from '@/lib/supabase'
import { chunk } from '../utils/chunk'
import { toClosedAtRange } from '../utils/dateRange'
import { normalizeOutcomeType, type OutcomeType } from '../utils/outcomeTypes'

export type RelatorioV2Filters = {
  closedFrom?: string | null
  closedTo?: string | null
  maxRows?: number
}

export type RelatorioV2Row = {
  opportunityId: number
  snapshotResultType: OutcomeType
  tp1Hit: boolean
  tp2Hit: boolean
  tp3Hit: boolean
  stopHit: boolean
  closedAt: string
  symbol: string | null
  timeframe: string | null
  targetTimeframe: string | null
  opportunityType: string | null
  signalType: string | null
  targetTrend: string | null
  confluenceStrength: number | null
  publishedAt: string | null
  closedReason: string | null
}

export type RelatorioV2Dataset = {
  rows: RelatorioV2Row[]
  totalMatched: number | null
  truncated: boolean
}

const DEFAULT_MAX_ROWS = 5000
const IDS_CHUNK_SIZE = 500

export async function fetchRelatorioV2Dataset(filters: RelatorioV2Filters): Promise<RelatorioV2Dataset> {
  const maxRows = clampInt(filters.maxRows ?? DEFAULT_MAX_ROWS, 1, 20000)
  const { fromIso, toIsoExclusive } = toClosedAtRange({
    closedFrom: filters.closedFrom ?? null,
    closedTo: filters.closedTo ?? null,
  })

  let q = supabase
    .from('signals_opportunity_results' as any)
    .select('opportunity_id,result_type,tp1_hit,tp2_hit,tp3_hit,stop_hit,closed_at', { count: 'exact' })
    .order('closed_at', { ascending: false })

  if (fromIso) q = q.gte('closed_at', fromIso)
  if (toIsoExclusive) q = q.lt('closed_at', toIsoExclusive)

  const { data: resultsRows, error: resultsErr, count } = await q.range(0, maxRows - 1)
  if (resultsErr) throw resultsErr
  const raw = Array.isArray(resultsRows) ? (resultsRows as any[]) : []
  const totalMatched = typeof count === 'number' ? count : null
  const truncated = typeof totalMatched === 'number' ? totalMatched > maxRows : raw.length >= maxRows

  const ids = raw
    .map((r) => Number(r?.opportunity_id))
    .filter((id) => Number.isFinite(id))

  const oppsById = await fetchOpportunitiesByIds(ids)

  const rows: RelatorioV2Row[] = raw.map((r) => {
    const opportunityId = Number(r?.opportunity_id)
    const opp = oppsById.get(opportunityId) || null
    return {
      opportunityId,
      snapshotResultType: normalizeOutcomeType(r?.result_type),
      tp1Hit: !!r?.tp1_hit,
      tp2Hit: !!r?.tp2_hit,
      tp3Hit: !!r?.tp3_hit,
      stopHit: !!r?.stop_hit,
      closedAt: String(r?.closed_at || ''),
      symbol: opp ? asText(opp.symbol) : null,
      timeframe: opp ? asText(opp.timeframe) : null,
      targetTimeframe: opp ? asText(opp.target_timeframe) : null,
      opportunityType: opp ? asText(opp.opportunity_type) : null,
      signalType: opp ? asText(opp.signal_type) : null,
      targetTrend: opp ? asText(opp.target_trend) : null,
      confluenceStrength: opp ? asNumberOrNull(opp.confluence_strength) : null,
      publishedAt: opp ? asText(opp.published_at) : null,
      closedReason: opp ? asText(opp.closed_reason) : null,
    }
  })

  return { rows, totalMatched, truncated }
}

async function fetchOpportunitiesByIds(ids: number[]): Promise<Map<number, any>> {
  const out = new Map<number, any>()
  const unique = Array.from(new Set(ids.filter((id) => Number.isFinite(id))))
  if (unique.length === 0) return out

  for (const part of chunk(unique, IDS_CHUNK_SIZE)) {
    const { data, error } = await supabase
      .from('trading_opportunities')
      .select(
        'id,symbol,timeframe,target_timeframe,opportunity_type,signal_type,target_trend,confluence_strength,published_at,closed_reason',
      )
      .in('id', part as any)
    if (error) throw error
    for (const r of Array.isArray(data) ? (data as any[]) : []) {
      const id = Number((r as any)?.id)
      if (Number.isFinite(id)) out.set(id, r)
    }
  }
  return out
}

function asText(v: any): string | null {
  const s = String(v ?? '').trim()
  return s ? s : null
}

function asNumberOrNull(v: any): number | null {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function clampInt(v: number, min: number, max: number): number {
  const n = Math.floor(Number(v))
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, n))
}
